import { booleanAttribute } from "#ce/utils/props";

const horizontalOptions = ["left", "center", "right"];

export const props = Object.freeze({
  /**
   * Suppress focus restoration on close when the caller owns the next focus target.
   *
   * @type {Boolean|String}
   * @default false
   */
  noRestoreFocus: booleanAttribute(),
  /**
   * Show the popup. When false the popup remains in the DOM but is hidden.
   *
   * @type {Boolean|String}
   * @default false
   */
  active: booleanAttribute(),
  /**
   * Disable the popup. Reserved for downstream consumers — does not affect rendering directly.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Anchor location relative to the trigger element. "bottom" centres the popup below; "bottom-left"/"bottom-right" align it to the trigger's start/end edge; "top" centres it above.
   *
   * @type {String}
   * @default "bottom"
   * @storybook-control select
   */
  location: {
    type: String,
    default: "bottom",
  },
  /**
   * Horizontal alignment when location is bottom. "center" centres horizontally; "left" aligns to the trigger's start edge; "right" aligns to the end edge.
   *
   * @type {String}
   * @default "center"
   * @storybook-control select
   */
  horizontalAlign: {
    type: String,
    default: "center",
    validator: (value) => horizontalOptions.includes(value),
  },
  /**
   * Render an arrow pointer between the trigger and the popup surface.
   *
   * @type {Boolean|String}
   * @default false
   */
  pointer: booleanAttribute(),
  /**
   * Render the popup inline (position: relative, full width, no shadow) instead of as a floating overlay. Used by consumers that embed the popup contents in a flow layout.
   *
   * @type {Boolean|String}
   * @default false
   */
  permanent: booleanAttribute(),
  /**
   * The element the popup is positioned against (the menu activator). Passed as a
   * DOM property by sp-menu and handed to Floating UI as its reference, so it
   * accepts either a real DOM element or a virtual element: any object exposing
   * `getBoundingClientRect(): DOMRect`, plus an optional `contextElement` (an
   * Element used for scroll/resize tracking). When absent the popup renders but is
   * not positioned, so floating callers must provide an anchor.
   *
   * @type {HTMLElement | { getBoundingClientRect: () => DOMRect, contextElement?: Element }}
   * @default undefined
   */
  anchor: {
    type: Object,
    default: undefined,
  },
});
